import { ArrowRight, Anchor, Plane, Truck } from "lucide-react";
import { TID } from "@/lib/testIds";

const HERO_IMG =
  "https://images.unsplash.com/photo-1769144256181-698b8f807066?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjB2aWV3JTIwY2FyZ28lMjBzaGlwJTIwY29udGFpbmVycyUyMG9jZWFufGVufDB8fHx8MTc4Mjc0MjEyNHww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid={TID.hero.section}
      className="relative min-h-[100svh] w-full overflow-hidden grain"
    >
      <img
        src={HERO_IMG}
        alt="JMR Global Logistics — international ocean freight cargo ship loaded with containers at port"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-blue-950/95 via-blue-950/70 to-blue-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-blue-950 via-transparent to-transparent" />

      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20 min-h-[100svh] flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-8 reveal in">
          <span className="h-px w-10 bg-sky-400" />
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-sky-400">
            INDIA · UNITED KINGDOM · MIDDLE EAST
          </span>
        </div>

        <h1
          data-testid={TID.hero.title}
          className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tighter max-w-5xl reveal in"
        >
          Connecting<br />
          Businesses.<br />
          Delivering <span className="text-sky-400">Excellence.</span>
        </h1>

        <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-200/90 leading-relaxed reveal in">
          JMR Global Logistics is a trusted international freight forwarding company delivering intelligent logistics solutions across air, ocean, and road transportation — helping businesses move cargo with reliability, efficiency, and complete visibility.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal in">
          <button
            data-testid={TID.hero.quoteBtn}
            onClick={() => scrollTo("quote")}
            className="group inline-flex items-center justify-center gap-3 bg-sky-400 text-blue-950 font-semibold px-8 py-4 hover:bg-sky-300 transition-all shadow-[6px_6px_0px_0px_rgba(255,255,255,0.25)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.25)]"
          >
            Request a Quote
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid={TID.hero.contactBtn}
            onClick={() => scrollTo("contact")}
            className="inline-flex items-center justify-center gap-3 border border-white/40 text-white font-semibold px-8 py-4 hover:bg-white/10 transition-colors"
          >
            Contact Our Team
          </button>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-3 max-w-2xl border-t border-white/15 reveal in">
          {[
            { Icon: Anchor, label: "Ocean Freight" },
            { Icon: Plane, label: "Air Freight" },
            { Icon: Truck, label: "Road Transport" },
          ].map((m, i) => (
            <div
              key={m.label}
              className={`flex items-center gap-3 py-5 ${i < 2 ? "border-r border-white/15" : ""} pr-4`}
            >
              <m.Icon className="w-5 h-5 text-sky-400" />
              <span className="text-white text-base font-semibold tracking-wide">{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 right-6 md:right-10 z-10 flex items-center gap-3 text-white/70">
        <span className="text-sm font-mono uppercase tracking-widest">Global Reach · Local Expertise · Seamless Logistics</span>
        <span className="w-2 h-2 bg-sky-400 animate-pulse" />
      </div>
    </section>
  );
}

